import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { ActionTabs, Icon, v2 } from '@fieldnation/platform-components';

import {
  AdditionalPartField,
  AdditionalPartValues,
  FieldName,
  NOTE_PLACEHOLDERS,
  Section,
  USAGE_CODES as USAGE_ENUMS,
} from '../PartsFormV3/types.d';
import {
  RETURN_INSTRUCTION_OPTIONS_NON_RETURNABLE,
  RETURN_INSTRUCTION_OPTIONS_RETURNABLE,
  USAGE_CODES,
  validateAdditionalFields,
} from '../PartsFormV3/utils';
import AdditionalPartFields from '../PartsFormV3/AdditionalPartFields';
import { NEW_PART_EXCLUDED_FIELDS } from '../PartsFormV3/constants';
import TrackingField from '../ShipmentForm/components/TrackingField';
import { getCarrierFromTrackingNumber } from '../ShipmentForm/utils';
import styles from './styles.scss';
import {
  isProviderPartsFormValid,
  RETURNABLE_NO,
  RETURNABLE_YES,
} from './utils';

const { TextInput, Select, TextArea } = v2;

export type ProviderPartsFormValue = {
  usageCode: string;
  partNumber: string;
  description: string;
  returnable: string;
  returnInstruction: string;
  returnTracking: string;
  returnCarrier: string;
  returnCarrierOtherName: string;
  note: string;
  additionalPartValues?: AdditionalPartValues;
};

export interface ProviderPartsFormProps {
  /** Partial field values merged into initial state once. Updates after mount are ignored. */
  value?: Partial<ProviderPartsFormValue>;
  /** Called whenever internal state changes, with the latest values and whether the form is valid. */
  onChange: (data: ProviderPartsFormValue, isValid: boolean) => void;
  /** Override the available usage codes (defaults to the standard six codes). */
  usageCodes?: string[];
  /** When true, forces the usage code to "Used" and disables the field. */
  lockUsageCode?: boolean;
  /** When true, renders additional part identifier fields driven by additionalPartFields config. */
  additionalFields?: boolean;
  /** Config array defining which additional fields to render and whether they are required. */
  additionalPartFields?: AdditionalPartField[];
}

const DEFAULT_VALUE: ProviderPartsFormValue = {
  usageCode: '',
  partNumber: '',
  description: '',
  returnable: '',
  returnInstruction: '',
  returnTracking: '',
  returnCarrier: '',
  returnCarrierOtherName: '',
  note: '',
};

const ProviderPartsForm = ({
  value,
  onChange,
  usageCodes = USAGE_CODES,
  lockUsageCode = false,
  additionalFields = false,
  additionalPartFields,
}: ProviderPartsFormProps): JSX.Element => {
  const [formData, setFormData] = useState<ProviderPartsFormValue>({
    ...DEFAULT_VALUE,
    ...value,
    ...(lockUsageCode ? { usageCode: USAGE_ENUMS.USED } : {}),
  });
  const [returnTrackingLink, setReturnTrackingLink] = useState('');
  const isReturnable = formData.returnable === RETURNABLE_YES;
  const usageSelected = Boolean(formData.usageCode);

  useEffect(() => {
    if (formData.returnTracking) {
      const { trackingLink } = getCarrierFromTrackingNumber(
        formData.returnTracking,
      );
      setReturnTrackingLink(trackingLink);
    }
  }, []);

  const returnInstructionLabel = useMemo(() => {
    const options = [
      ...RETURN_INSTRUCTION_OPTIONS_RETURNABLE,
      ...RETURN_INSTRUCTION_OPTIONS_NON_RETURNABLE,
    ];
    return (
      options.find((opt) => opt.value === formData.returnInstruction)?.label ||
      formData.returnInstruction ||
      ''
    );
  }, [formData.returnInstruction]);

  const showReturnSection =
    isReturnable &&
    !!formData.usageCode &&
    formData.usageCode !== USAGE_ENUMS.NOT_RECEIVED;

  const emit = useCallback(
    (next: ProviderPartsFormValue) => {
      const baseValid = isProviderPartsFormValid(next);
      const addlValid =
        !additionalFields ||
        validateAdditionalFields(additionalPartFields, next.additionalPartValues, next.usageCode);
      onChange(next, baseValid && addlValid);
    },
    [onChange, additionalFields, additionalPartFields],
  );

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updated = { ...formData, [e.target.id]: e.target.value };
    setFormData(updated);
    emit(updated);
  };

  const onReturnableToggle = (val: typeof RETURNABLE_YES | typeof RETURNABLE_NO) => {
    const returnInstructionOptions =
      val === RETURNABLE_YES
        ? RETURN_INSTRUCTION_OPTIONS_RETURNABLE
        : RETURN_INSTRUCTION_OPTIONS_NON_RETURNABLE;
    const newReturnInstruction = returnInstructionOptions[0]?.value || '';
    const updated = {
      ...formData,
      returnable: val,
      returnInstruction: newReturnInstruction,
      returnTracking: val === RETURNABLE_NO ? '' : formData.returnTracking,
    };
    setFormData(updated);
    emit(updated);
  };

  const onUsageCodeSelect = (selectedUsageCode: string) => {
    const updated = { ...formData, usageCode: selectedUsageCode };
    setFormData(updated);
    emit(updated);
  };

  const onReturnTrackingChange = (carrierInfo: {
    name: string;
    tracking: string;
    tracking_link?: string;
    other_name?: string;
  }) => {
    const updated = {
      ...formData,
      returnTracking: carrierInfo.tracking,
      returnCarrier: carrierInfo?.name || '',
      returnCarrierOtherName: carrierInfo?.other_name ?? '',
    };
    setFormData(updated);
    setReturnTrackingLink(carrierInfo.tracking_link || '');
    emit(updated);
  };

  const onNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const updated = { ...formData, note: e.target.value };
    setFormData(updated);
    emit(updated);
  };

  const updateAdditionalValue = (
    section: Section,
    fieldName: FieldName,
    val: string,
  ) => {
    const updated: ProviderPartsFormValue = {
      ...formData,
      additionalPartValues: {
        ...formData.additionalPartValues,
        [section]: {
          ...formData.additionalPartValues?.[section],
          [fieldName]: val,
        },
      },
    };
    setFormData(updated);
    emit(updated);
  };

  return (
    <div className={styles['provider-parts-form']}>
      <p className={styles['section-label']}>NEW PART</p>

      {/* Usage code */}
      <div className={styles['input-gap']}>
        <Select
          label="Usage code"
          options={usageCodes.map((code) => ({ label: code, value: code }))}
          onChange={(option) => onUsageCodeSelect(String(option.value ?? ''))}
          value={formData.usageCode}
          disabled={lockUsageCode}
        />
      </div>

      {/* Additional fields — new part (rma_number excluded, only applies to defective part) */}
      {additionalFields && (
        <AdditionalPartFields
          fields={additionalPartFields}
          values={formData.additionalPartValues}
          section="new"
          excludeFields={NEW_PART_EXCLUDED_FIELDS}
          onChange={updateAdditionalValue}
          fieldClassName={styles['input-gap']}
        />
      )}

      {/* Number */}
      <div className={styles['input-gap']}>
        <TextInput
          id="partNumber"
          label="Number"
          onChange={onInputChange}
          value={formData.partNumber}
        />
      </div>

      {/* Description */}
      <div className={styles['input-gap']}>
        <TextInput
          id="description"
          label="Description"
          onChange={onInputChange}
          value={formData.description}
        />
      </div>

      {/* Do you need to return the defective part? */}
      <div className={styles['returnable-section']}>
        <p className={styles['return-question']}>
          Do you need to return the defective part?
        </p>
        <ActionTabs
          items={[
            {
              label: RETURNABLE_YES,
              active: formData.returnable === RETURNABLE_YES,
              onClick: () => onReturnableToggle(RETURNABLE_YES),
            },
            {
              label: RETURNABLE_NO,
              active: formData.returnable === RETURNABLE_NO,
              onClick: () => onReturnableToggle(RETURNABLE_NO),
            },
          ]}
        />
      </div>

      {/* DEFECTIVE PART return section */}
      {showReturnSection && (
        <>
          <p
            className={`${styles['section-label']} ${styles['defective-part-section-label']}`}
          >
            DEFECTIVE PART
          </p>

          {returnInstructionLabel && (
            <div className={styles['return-instruction']}>
              <Icon name="time" size={22} />
              <span className={styles['return-instruction-text']}>
                {returnInstructionLabel}
                .
              </span>
            </div>
          )}

          <div className={styles['input-gap']}>
            <TrackingField
              inputLabel="Return tracking number"
              value={{
                name: formData.returnCarrier || '',
                tracking: formData.returnTracking ?? '',
                other_name: formData.returnCarrierOtherName || '',
                tracking_link: returnTrackingLink || '',
              }}
              onChange={onReturnTrackingChange}
              disabled={false}
              errors={{ name: false, tracking: false }}
            />
          </div>
        </>
      )}

      {/* Additional fields — defective part (all three fields, shown when usage code selected) */}
      {additionalFields && usageSelected && (
        <AdditionalPartFields
          fields={additionalPartFields}
          values={formData.additionalPartValues}
          section="defective"
          onChange={updateAdditionalValue}
          fieldClassName={styles['input-gap']}
        />
      )}

      <hr className={styles.divider} />

      {/* Notes */}
      {formData.usageCode && (
        <div className={styles['input-gap']}>
          <TextArea
            id="notes"
            label="Notes"
            placeholder={NOTE_PLACEHOLDERS[formData.usageCode]}
            value={formData.note || ''}
            onChange={onNoteChange}
            optional
          />
        </div>
      )}
    </div>
  );
};

export default ProviderPartsForm;
